import { ChangeDetectorRef, Component, ElementRef, Input, OnChanges, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { LabeledInputComponent } from "../../../../shared/components/labeled-input/labeled-input.component";
import { CommonModule, DecimalPipe } from '@angular/common';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { CustomDropdownComponent } from '../../../../shared/components/custom-dropdown/custom-dropdown.component';
import { AdditionalNoteFieldsElectricityCreate, AdditionalNoteFieldsElectricityGet, AdminChangesLog, CommodityType, ConsumptionClass, ContractType, ElectricityInvoice, FileType, InvoiceElectricityData, InvoiceElectricityExtractedData, InvoiceElectricityPODGet, InvoiceStatus, InvoiceVariableStatus, NetworkTensionType, PriceType, SupplierGet, VisibilityStatus } from '../../../../core/interfaces/invoice-interfaces';
import { CommonValidatorService } from '../../../../core/services/validators/common-validator.service';
import { DateConversionService } from '../../../../core/services/date-conversion.service';
import { ToastComponent } from '../../../../shared/components/toast/toast.component';
import { Location } from '../../../../core/interfaces/location';
import { CustomDateInputComponent } from "../../../../shared/components/custom-date-input/custom-date-input.component";
import { lastValueFrom } from 'rxjs';
import { InvoiceService } from '../../../../core/api/invoice.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiErrorService } from '../../../../core/api/api-error.service';
import { CustomNumberInputComponent } from "../../../../shared/components/custom-number-input/custom-number-input.component";
import { TextareaInputComponent } from '../../../../shared/components/textarea-input/textarea-input.component';
import { DropDownSectionComponent } from "../../../../shared/components/drop-down-section/drop-down-section.component";
import { InvoiceVariableStatusComponent } from "../../../admin/components/invoice-variable-status/invoice-variable-status.component";
import { EditCancelButtonComponent } from "../../../../shared/components/edit-cancel-button/edit-cancel-button.component";
import { CollapseArrowComponent } from "../../../../shared/components/collapse-arrow/collapse-arrow.component";
import { DeltaValueComponent } from "../../../admin/components/delta-value/delta-value.component";
import { FilesService } from '../../../../core/api/files.service';
import { FileResponse, FullFileDataResponse } from '../../../../core/interfaces/file-upload-interfaces';
import { FileDetailsViewComponent } from "../../../admin/components/file-details-view/file-details-view.component";
import { ToggleButtonComponent } from "../../../../shared/components/toggle-button/toggle-button.component";
import { NavigationShareService } from '../../../../core/services/navigation-share.service';
import { UtilsService } from '../../../../core/services/utils.service';
import { UserDataService } from '../../../../core/services/user-data.service';
import { CustomButtonComponent } from "../../../../shared/components/custom-button/custom-button.component";
import { MandatoryDataMissingComponent } from "../../../admin/components/mandatory-data-missing/mandatory-data-missing.component";
import { InfoTooltipComponent } from "../../../../shared/components/info-tooltip/info-tooltip.component";

@Component({
  selector: 'app-edit-electricity-invoice',
  standalone: true,
  imports: [LabeledInputComponent, CommonModule, TranslocoPipe, FormsModule, DialogModule, CustomDropdownComponent, CustomDateInputComponent, CustomNumberInputComponent, TextareaInputComponent, DropDownSectionComponent, InvoiceVariableStatusComponent, EditCancelButtonComponent, CollapseArrowComponent, DeltaValueComponent, FileDetailsViewComponent, ToggleButtonComponent, CustomButtonComponent, MandatoryDataMissingComponent, InfoTooltipComponent],
  providers: [DecimalPipe],
  templateUrl: './edit-electricity-invoice.component.html',
  styleUrl: './edit-electricity-invoice.component.css'
})
export class EditElectricityInvoiceComponent implements OnChanges {
  private readonly VARIABLE_PRICE_TYPES = [PriceType.VAR, PriceType.VAR_PUN, PriceType.VAR_PUNH];
  private readonly FIX_PRICE_TYPES = [PriceType.FIX]

  constructor(
    private validator: CommonValidatorService,
    private dateConversion: DateConversionService,
    private transloco: TranslocoService,
    private invoiceService: InvoiceService,
    private apiError: ApiErrorService,
    private filesService: FilesService,
    private decimalPipe: DecimalPipe,
    private navigationShareService: NavigationShareService,
    private utilsService: UtilsService,
    private userData: UserDataService,
    private cd: ChangeDetectorRef
  ) { }

  @Input() invoice: ElectricityInvoice | null = null;
  @Input() listOfLocations: Location[] = [];
  @Input() toast!: ToastComponent;
  @Input() reloadCallbackFunction!: () => void;

  saveClicked: boolean = false;
  successful: boolean = false;

  // Editing Options
  isEditingInvoiceData: boolean = false;

  // Saving
  savingInvoice: boolean = false;

  // Table Collapses
  isTotalMaterialCollapsed: boolean = false;
  isDispatchAndMarketCapCollapsed: boolean = false;
  isTMSCollapsed: boolean = false;
  isTaxesCollapsed: boolean = false;
  isInvoiceChargesCollapsed: boolean = false;
  isVATCollapsed: boolean = false;
  isSupplierCollapsed: boolean = false;
  isEnergyChargeCollapsed: boolean = false;
  isPowerChargeCollapsed: boolean = false;

  // View all components
  viewAllComponents: boolean = false;

  // Dropdown
  locationsDropdownOptions: string[] = [];
  selectedLocationDropdown: number | undefined;

  contractTypeDropdownOptions: string[] = [];
  priceTypeDropdownOptions: string[] = [];
  networkTensionDropdownOptions: string[] = [];

  consumptionClassDropdownOptions: string[] = [];
  selectedConsumptionClassDropdown: number | undefined;

  // Base invoice inputs
  invoiceNumberInput: string = '';
  totalConsumptionInput: string = '';
  invoiceAmountActualInput: string = '';
  invoiceAmountGrossInput: string = '';
  forPeriodDate: Date | undefined;
  totalDiscountInput: string = '';
  vatRateInput: string = '';
  vatAmountInput: string = '';
  nontaxableAmountInput: string = '';

  adminNotesInput: string = '';


  // Errors
  invoiceNumberInputError: string | null = null;
  totalConsumptionInputError: string | null = null;
  invoiceAmountActualInputError: string | null = null;
  invoiceAmountGrossInputError: string | null = null;
  forPeriodInputError: string | null = null;
  totalDiscountInputError: string | null = null;
  vatRateInputError: string | null = null;
  vatAmountInputError: string | null = null;
  nontaxableAmountInputError: string | null = null;
  locationDropdownError: string | null = null;
  tmsChargesInputWarnings: (string | null)[] = [];
  networkTensionInputWarnings: (string | null)[] = [];
  tensionFactorInputWarnings: (string | null)[] = [];
  energyChargePriceInputWarnings: (string | null)[] = [];
  powerChargePriceInputWarnings: (string | null)[] = [];
  powerKwContractedInputWarnings: (string | null)[] = [];
  powerKwMaxConsumptionInputWarnings: (string | null)[] = [];
  totalTaxesInputWarnings: (string | null)[] = [];
  monthConsumptionF1InputWarnings: (string | null)[] = [];
  monthConsumptionF2InputWarnings: (string | null)[] = [];
  monthConsumptionF3InputWarnings: (string | null)[] = [];
  priceVarF1InputWarnings: (string | null)[] = [];
  priceVarF2InputWarnings: (string | null)[] = [];
  priceVarF3InputWarnings: (string | null)[] = [];
  priceVarF0InputWarnings: (string | null)[] = [];
  priceVarPInputWarnings: (string | null)[] = [];
  priceVarPoInputWarnings: (string | null)[] = [];
  monthConsumptionPInputWarnings: (string | null)[] = [];
  monthConsumptionPoInputWarnings: (string | null)[] = [];
  totalConsumptionInputWarning: string | null = null;
  invoiceAmountActualInputWarning: string | null = null;
  priceTypeDropdownWarning: (string | null)[] = [];
  networkTensionDropdownWarning: (string | null)[] = [];
  contractTypeDropdownWarning: (string | null)[] = [];
  priceFixF1InputWarnings: (string | null)[] = [];
  priceFixF2InputWarnings: (string | null)[] = [];
  priceFixF3InputWarnings: (string | null)[] = [];
  priceFixF0InputWarnings: (string | null)[] = [];
  priceFixPInputWarnings: (string | null)[] = [];
  priceFixPoInputWarnings: (string | null)[] = [];
  taxRate1InputWarnings: (string | null)[] = [];
  taxRate2InputWarnings: (string | null)[] = [];
  monthConsumptionF0InputWarnings: (string | null)[] = [];

  adminNotesInputError: string | null = null;




  // Editable form values extracted from each pod
  editedPods: {
    month_consumption_f1?: string;
    month_consumption_f2?: string;
    month_consumption_f3?: string;
    month_consumption_f0?: string;
    month_consumption_p?: string;
    month_consumption_po?: string;

    pun_price_f1?: string;
    pun_price_f2?: string;
    pun_price_f3?: string;
    pun_price_f0?: string;
    pun_price_p?: string;
    pun_price_po?: string;

    spread_f1?: string;
    spread_f2?: string;
    spread_f3?: string;
    spread_f0?: string;
    spread_p?: string;
    spread_po?: string;

    price_var_f1?: string;
    price_var_f2?: string;
    price_var_f3?: string;
    price_var_f0?: string;
    price_var_p?: string;
    price_var_po?: string;

    price_fix_f1?: string;
    price_fix_f2?: string;
    price_fix_f3?: string;
    price_fix_p?: string;
    price_fix_po?: string;
    price_fix_f0?: string;

    contract_type?: string;
    tariff_code?: string;
    consumption_class?: string;
    selectedConsumptionClassDropdown?: number | undefined;
    selectedPriceTypeDropdown?: number | undefined;
    selectedNetworkTensionDropdown?: number | undefined;

    network_tension?: string;
    tension_factor?: string;
    power_kw_available?: string;
    power_kw_contracted?: string;
    power_kw_max_consumption?: string;
    total_material_costs?: string;
    dispatch_costs?: string;
    dispatching_factor?: string;
    market_capacity_p?: string;
    market_capacity_op?: string;
    fixed_charge?: string;
    energy_charge_price?: string;
    power_charge_price?: string;
    reactive_energy_costs?: string;
    fixed_costs?: string;
    additional_charges?: string;
    taxrate_1?: string;
    taxrate_2?: string;

    pod_id?: string;
    available_capacity?: string;
    voltage?: string;
    excise_taxes?: string;
    vat_rate?: string;
    vat_amount_actual?: string;

    tms_charges?: string;

    total_taxes?: string;

    modulation_charge?: string;

    selectedContractTypeDropdown?: number | undefined;

    supplierData: {
      name?: string;
      vat_id?: string;
      contact_name?: string;
      contact_number?: string;
      email?: string;
      start_of_supply?: Date;
      end_of_supply?: Date;
    };

    additionalNoteFields: {
      invoice_charges?: string;
      total_material_cost?: string;

      network_loss_f0?: string;
      network_loss_f1?: string;
      network_loss_f2?: string;
      network_loss_f3?: string;
      network_loss_p?: string;
      network_loss_po?: string;

      dispatch_and_marketing_capacity?: string;

      energy_charge?: string;
      power_charge?: string;
    }
  }[] = [];

  // File Summary
  loadingFileSummary: boolean = true;
  fileDetailsResponse: FullFileDataResponse | null = null;

  // Type helpers
  ContractType = ContractType;
  PriceType = PriceType;
  InvoiceVariableStatus = InvoiceVariableStatus;

  // DropDown Sections
  @ViewChild('invoiceDataDropdown') invoiceDataDropdown?: DropDownSectionComponent;
  @ViewChild('invoiceDataDropdown', { read: ElementRef }) invoiceDataDropdownEl?: ElementRef;

  @ViewChildren('podSection') podSectionsDropdown?: QueryList<DropDownSectionComponent>;

  @ViewChild('spreadzero', { read: ElementRef }) spreadf0Element?: ElementRef;
  @ViewChild('spreadone', { read: ElementRef }) spreadf1Element?: ElementRef;
  @ViewChild('spreadp', { read: ElementRef }) spreadpElement?: ElementRef;

  // Mandatory data
  mandatoryDataMissing: boolean = false;

  // Additiona fields expresion changes
  exprByField: Record<string, string> = {};

  // Track open pods
  private openedPodSectionIndexes = new Set<number>();

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['invoice'] || changes['listOfLocations']) && this.invoice) {
      this.updateEditData(this.invoice, this.listOfLocations);
    }
  }

  updateEditData(invoice: ElectricityInvoice, listOfLocations: Location[]): void {
    this.successful = false;
    this.saveClicked = false;
    this.invoice = invoice;
    this.populateStaticDropdowns();
    this.populateInputsFromInvoice(invoice);
    this.populateLocationsDropdown();
    this.validateRequiredFields();

    this.cd.detectChanges();

    setTimeout(() => {
      this.restoreOpenedPodSections();
    });
  }

  resetInvoiceData() {
    if (this.invoice) {
      this.updateEditData(this.invoice, this.listOfLocations);
    }
  }

  populateLocationsDropdown(): void {
    this.locationsDropdownOptions = [];

    this.listOfLocations.forEach((location, index) => {
      const label = `${location.street ?? ''} ${location.number ?? ''}, ${location.postal_code ?? ''} ${location.city ?? ''}`;
      this.locationsDropdownOptions.push(label);

      if (location.id === this.invoice?.location_id) {
        this.selectedLocationDropdown = index;
      }
    });
  }

  populateStaticDropdowns() {
    this.contractTypeDropdownOptions = [];
    this.contractTypeDropdownOptions = Object.values(ContractType);

    this.priceTypeDropdownOptions = [];
    this.priceTypeDropdownOptions = Object.values(PriceType)

    this.consumptionClassDropdownOptions = [];
    this.consumptionClassDropdownOptions = Object.values(ConsumptionClass)

    this.networkTensionDropdownOptions = [];
    this.networkTensionDropdownOptions = Object.values(NetworkTensionType)
  }

  populateInputsFromInvoice(invoice: ElectricityInvoice): void {
    this.invoiceNumberInput = invoice.invoice_number ?? '';
    this.totalConsumptionInput = invoice.total_consumption?.toString() ?? '';
    this.invoiceAmountActualInput = invoice.invoice_amount_net_actual?.toString() ?? '';
    this.invoiceAmountGrossInput = invoice.invoice_amount_gross_actual?.toString() ?? '';
    this.forPeriodDate = invoice.for_period ? new Date(invoice.for_period) : undefined;
    this.totalDiscountInput = invoice.total_discount_amount?.toString() ?? '';
    this.vatRateInput = invoice.vat_rate?.toString() ?? '';
    this.vatAmountInput = invoice.vat_amount_actual?.toString() ?? '';
    this.nontaxableAmountInput = invoice.nontaxable_amount?.toString() ?? '';
    this.adminNotesInput = invoice.admin_notes ?? '';
    this.selectedConsumptionClassDropdown = this.consumptionClassDropdownOptions
      .findIndex(opt => opt.toUpperCase() === (invoice.consumption_class ?? '').toUpperCase());

    this.editedPods = invoice.pods.map(p => ({
      month_consumption_f1: p.month_consumption_f1?.toString() ?? '',
      month_consumption_f2: p.month_consumption_f2?.toString() ?? '',
      month_consumption_f3: p.month_consumption_f3?.toString() ?? '',
      month_consumption_f0: p.month_consumption_f0?.toString() ?? '',
      month_consumption_p: p.month_consumption_p?.toString() ?? '',
      month_consumption_po: p.month_consumption_po?.toString() ?? '',

      pun_price_f1: p.pun_price_f1?.toString() ?? '',
      pun_price_f2: p.pun_price_f2?.toString() ?? '',
      pun_price_f3: p.pun_price_f3?.toString() ?? '',
      pun_price_f0: p.pun_price_f0?.toString() ?? '',
      pun_price_p: p.pun_price_p?.toString() ?? '',
      pun_price_po: p.pun_price_po?.toString() ?? '',

      spread_f1: p.spread_f1?.toString() ?? '',
      spread_f2: p.spread_f2?.toString() ?? '',
      spread_f3: p.spread_f3?.toString() ?? '',
      spread_f0: p.spread_f0?.toString() ?? '',
      spread_p: p.spread_p?.toString() ?? '',
      spread_po: p.spread_po?.toString() ?? '',

      price_var_f1: p.price_var_f1?.toString() ?? '',
      price_var_f2: p.price_var_f2?.toString() ?? '',
      price_var_f3: p.price_var_f3?.toString() ?? '',
      price_var_f0: p.price_var_f0?.toString() ?? '',
      price_var_p: p.price_var_p?.toString() ?? '',
      price_var_po: p.price_var_po?.toString() ?? '',

      price_fix_f1: p.price_fix_f1?.toString() ?? '',
      price_fix_f2: p.price_fix_f2?.toString() ?? '',
      price_fix_f3: p.price_fix_f3?.toString() ?? '',
      price_fix_p: p.price_fix_p?.toString() ?? '',
      price_fix_po: p.price_fix_po?.toString() ?? '',
      price_fix_f0: p.price_fix_f0?.toString() ?? '',

      tariff_code: p.tariff_code ?? undefined,
      price_type: p.price_type ?? undefined,
      network_tension: p.network_tension ?? undefined,
      tension_factor: p.tension_factor?.toString() ?? '',
      power_kw_available: p.power_kw_available?.toString() ?? '',
      power_kw_contracted: p.power_kw_contracted?.toString() ?? '',
      power_kw_max_consumption: p.power_kw_max_consumption?.toString() ?? '',
      total_material_costs: p.total_material_costs?.toString() ?? '',
      dispatch_costs: p.dispatch_costs?.toString() ?? '',
      dispatching_factor: p.dispatching_factor?.toString() ?? '',
      market_capacity_p: p.market_capacity_p?.toString() ?? '',
      market_capacity_op: p.market_capacity_op?.toString() ?? '',
      fixed_charge: p.fixed_charge?.toString() ?? '',
      energy_charge_price: p.energy_charge_price?.toString() ?? '',
      power_charge_price: p.power_charge_price?.toString() ?? '',
      reactive_energy_costs: p.reactive_energy_costs?.toString() ?? '',
      fixed_costs: p.fixed_costs?.toString() ?? '',
      additional_charges: p.additional_charges?.toString() ?? '',
      taxrate_1: p.taxrate_1?.toString() ?? '',
      taxrate_2: p.taxrate_2?.toString() ?? '',
      pod_id: p.pod_id?.toString() ?? '',

      vat_rate: p.vat_rate?.toString() ?? '',
      vat_amount_actual: p.vat_amount_actual?.toString() ?? '',

      tms_charges: p.tms_charges?.toString() ?? '',
      total_taxes: p.total_taxes?.toString() ?? '',

      modulation_charge: p.modulation_charge?.toString() ?? '',

      selectedContractTypeDropdown: (i => i !== -1 ? i : undefined)(this.contractTypeDropdownOptions.findIndex(option => option === p.contract_type)),
      selectedPriceTypeDropdown: (i => i !== -1 ? i : undefined)(this.priceTypeDropdownOptions.findIndex(option => option === p.price_type)),
      selectedConsumptionClassDropdown: (i => i !== -1 ? i : undefined)(this.consumptionClassDropdownOptions.findIndex(option => option.toUpperCase() === p.consumption_class?.toUpperCase())),
      selectedNetworkTensionDropdown: (i => i !== -1 ? i : undefined)(this.networkTensionDropdownOptions.findIndex(option => option === p.network_tension)),


      supplierData: {
        name: p.supplier?.name ?? '',
        vat_id: p.supplier?.vat_id ?? '',
        contact_name: p.supplier?.contact_name ?? '',
        contact_number: p.supplier?.contact_number ?? '',
        email: p.supplier?.email ?? '',
        start_of_supply: p.supplier?.start_of_supply ? new Date(p.supplier.start_of_supply) : undefined,
        end_of_supply: p.supplier?.end_of_supply ? new Date(p.supplier.end_of_supply) : undefined,
      },

      additionalNoteFields: {
        invoice_charges: p.additional_note_fields?.invoice_charges?.toString() || '',
        total_material_cost: p.additional_note_fields?.total_material_cost?.toString() || '',
        network_loss_f0: p.additional_note_fields?.network_loss_f0?.toString() || '',
        network_loss_f1: p.additional_note_fields?.network_loss_f1?.toString() || '',
        network_loss_f2: p.additional_note_fields?.network_loss_f2?.toString() || '',
        network_loss_f3: p.additional_note_fields?.network_loss_f3?.toString() || '',
        network_loss_p: p.additional_note_fields?.network_loss_p?.toString() || '',
        network_loss_po: p.additional_note_fields?.network_loss_po?.toString() || '',
        dispatch_and_marketing_capacity: p.additional_note_fields?.dispatch_and_marketing_capacity?.toString() || '',
        energy_charge: p.additional_note_fields?.energy_charge?.toString() || '',
        power_charge: p.additional_note_fields?.power_charge?.toString() || ''
      },
    }));
  }

  getAdditionalNoteFieldsFromInputs(index: number, additionalNote: AdditionalNoteFieldsElectricityGet | null): AdditionalNoteFieldsElectricityCreate {
    const additionalNoteFields: AdditionalNoteFieldsElectricityCreate = {
      total_material_cost: parseFloat(this.editedPods[index].additionalNoteFields.total_material_cost || '') || (additionalNote?.total_material_cost ?? null),
      network_loss_f0: parseFloat(this.editedPods[index].additionalNoteFields.network_loss_f0 || '') || (additionalNote?.network_loss_f0 ?? null),
      network_loss_f1: parseFloat(this.editedPods[index].additionalNoteFields.network_loss_f1 || '') || (additionalNote?.network_loss_f1 ?? null),
      network_loss_f2: parseFloat(this.editedPods[index].additionalNoteFields.network_loss_f2 || '') || (additionalNote?.network_loss_f3 ?? null),
      network_loss_f3: parseFloat(this.editedPods[index].additionalNoteFields.network_loss_f3 || '') || (additionalNote?.network_loss_f0 ?? null),
      network_loss_p: parseFloat(this.editedPods[index].additionalNoteFields.network_loss_p || '') || (additionalNote?.network_loss_p ?? null),
      network_loss_po: parseFloat(this.editedPods[index].additionalNoteFields.network_loss_po || '') || (additionalNote?.network_loss_po ?? null),
      dispatch_and_marketing_capacity: parseFloat(this.editedPods[index].additionalNoteFields.dispatch_and_marketing_capacity || '') || (additionalNote?.dispatch_and_marketing_capacity ?? null),
      energy_charge: parseFloat(this.editedPods[index].additionalNoteFields.energy_charge || '') || (additionalNote?.energy_charge ?? null),
      power_charge: parseFloat(this.editedPods[index].additionalNoteFields.power_charge || '') || (additionalNote?.power_charge ?? null),
      invoice_charges: parseFloat(this.editedPods[index].additionalNoteFields.invoice_charges || '') || (additionalNote?.invoice_charges ?? null)
    };
    return additionalNoteFields;
  }

  getElectricityInvoiceDataFromInputs(): InvoiceElectricityData {
    const selectedLocation = this.navigationShareService.getState("invoiceReviewLocation");

    const invoiceData: InvoiceElectricityData = {
      id: this.invoice?.id ?? '',
      processed_at: new Date().toISOString(),
      type: CommodityType.electricity,
      file_type: FileType.invoice,
      data: {
        location: {
          street: selectedLocation.street ?? null,
          number: selectedLocation.number ?? null,
          postal_code: selectedLocation.postal_code ?? null,
          city: selectedLocation.city ?? null,
          province: selectedLocation.province ?? null,
          latitude: selectedLocation.latitude ?? null,
          longitude: selectedLocation.longitude ?? null
        },
        invoice_data: {
          for_period: this.invoice!.for_period,
          invoice_number: this.invoiceNumberInput,
          total_consumption: parseFloat(this.totalConsumptionInput),
          invoice_amount_net_actual: parseFloat(this.invoiceAmountActualInput),
          invoice_amount_gross_actual: parseFloat(this.invoiceAmountGrossInput),
          additional_charges: this.invoice?.pods[0].additional_charges ?? null,
          total_discount_amount: parseFloat(this.totalDiscountInput) ?? null,
          vat_rate: parseFloat(this.vatRateInput) ?? null,
          vat_amount_actual: parseFloat(this.vatAmountInput) ?? null,
          nontaxable_amount: parseFloat(this.nontaxableAmountInput) ?? null,
          consumption_class: this.consumptionClassDropdownOptions[this.selectedConsumptionClassDropdown!] || null,
          admin_notes: this.adminNotesInput || null
        },
        pod_data: this.editedPods.map((pod, i) => {
          const original = this.invoice?.pods[i];
          const selectedContractType = this.contractTypeDropdownOptions[pod.selectedContractTypeDropdown!].toUpperCase();
          const selectedPriceType = this.priceTypeDropdownOptions[pod.selectedPriceTypeDropdown!].toUpperCase();
          const selectedNetworkTension = this.networkTensionDropdownOptions[pod.selectedNetworkTensionDropdown!].toUpperCase();
          const selectedConsumptionClass =
            pod.selectedConsumptionClassDropdown != null
              ? this.consumptionClassDropdownOptions[pod.selectedConsumptionClassDropdown] ?? null
              : null;
          return {
            supplier_name: pod.supplierData.name ?? null,
            total_taxes: parseFloat(pod.total_taxes ?? '') ?? null,
            tms_charges: parseFloat(pod.tms_charges ?? '') ?? null,

            contract_type: selectedContractType ?? null,
            tariff_code: pod?.tariff_code ?? null,

            month_consumption_f1: parseFloat(pod.month_consumption_f1 ?? '') ?? null,
            month_consumption_f2: parseFloat(pod.month_consumption_f2 ?? '') ?? null,
            month_consumption_f3: parseFloat(pod.month_consumption_f3 ?? '') ?? null,
            month_consumption_f0: parseFloat(pod.month_consumption_f0 ?? '') ?? null,
            month_consumption_p: parseFloat(pod.month_consumption_p ?? '') ?? null,
            month_consumption_po: parseFloat(pod.month_consumption_po ?? '') ?? null,

            price_type: selectedPriceType ?? null,
            consumption_class: selectedConsumptionClass ?? null,

            pun_price_f1: parseFloat(pod.pun_price_f1 ?? '') ?? null,
            pun_price_f2: parseFloat(pod.pun_price_f2 ?? '') ?? null,
            pun_price_f3: parseFloat(pod.pun_price_f3 ?? '') ?? null,
            pun_price_f0: parseFloat(pod.pun_price_f0 ?? '') ?? null,
            pun_price_p: parseFloat(pod.pun_price_p ?? '') ?? null,
            pun_price_po: parseFloat(pod.pun_price_po ?? '') ?? null,

            spread_f1: parseFloat(pod.spread_f1 ?? '') ?? null,
            spread_f2: parseFloat(pod.spread_f2 ?? '') ?? null,
            spread_f3: parseFloat(pod.spread_f3 ?? '') ?? null,
            spread_f0: parseFloat(pod.spread_f0 ?? '') ?? null,
            spread_po: parseFloat(pod.spread_po ?? '') ?? null,
            spread_p: parseFloat(pod.spread_p ?? '') ?? null,

            price_var_f1: parseFloat(pod.price_var_f1 ?? '') ?? null,
            price_var_f2: parseFloat(pod.price_var_f2 ?? '') ?? null,
            price_var_f3: parseFloat(pod.price_var_f3 ?? '') ?? null,
            price_var_f0: parseFloat(pod.price_var_f0 ?? '') ?? null,
            price_var_p: parseFloat(pod.price_var_p ?? '') ?? null,
            price_var_po: parseFloat(pod.price_var_po ?? '') ?? null,

            price_fix_f1: parseFloat(pod.price_fix_f1 ?? '') ?? null,
            price_fix_f2: parseFloat(pod.price_fix_f2 ?? '') ?? null,
            price_fix_f3: parseFloat(pod.price_fix_f3 ?? '') ?? null,
            price_fix_f0: parseFloat(pod.price_fix_f0 ?? '') ?? null,
            price_fix_p: parseFloat(pod.price_fix_p ?? '') ?? null,
            price_fix_po: parseFloat(pod.price_fix_po ?? '') ?? null,

            network_tension: selectedNetworkTension ?? null,
            tension_factor: parseFloat(pod.tension_factor ?? '') ?? null,
            power_kw_available: parseFloat(pod.power_kw_available ?? '') ?? null,
            power_kw_contracted: parseFloat(pod.power_kw_contracted ?? '') ?? null,
            power_kw_max_consumption: parseFloat(pod.power_kw_max_consumption ?? '') ?? null,
            total_material_costs: parseFloat(pod.total_material_costs ?? '') ?? null,
            dispatch_costs: parseFloat(pod.dispatch_costs ?? '') ?? null,
            dispatching_factor: parseFloat(pod.dispatching_factor ?? '') ?? null,

            market_capacity_p: parseFloat(pod.market_capacity_p ?? '') ?? null,
            market_capacity_op: parseFloat(pod.market_capacity_op ?? '') ?? null,
            fixed_charge: parseFloat(pod.fixed_charge ?? '') ?? null,
            energy_charge_price: parseFloat(pod.energy_charge_price ?? '') ?? null,
            power_charge_price: parseFloat(pod.power_charge_price ?? '') ?? null,

            reactive_energy_costs: parseFloat(pod.reactive_energy_costs ?? '') ?? null,
            fixed_costs: parseFloat(pod.fixed_costs ?? '') ?? null,
            additional_charges: parseFloat(pod.additional_charges ?? '') ?? null,

            taxrate_1: parseFloat(pod.taxrate_1 ?? '') ?? null,
            taxrate_2: parseFloat(pod.taxrate_2 ?? '') ?? null,

            modulation_charge: parseFloat(pod.modulation_charge ?? '') ?? null,

            location: {
              street: selectedLocation.street ?? null,
              number: selectedLocation.number ?? null,
              postal_code: selectedLocation.postal_code ?? null,
              city: selectedLocation.city ?? null,
              province: selectedLocation.province ?? null
            },

            supplier: {
              name: pod.supplierData.name ?? null,
              vat_id: pod.supplierData.vat_id ?? null,
              contact_number: pod.supplierData.contact_number ?? null,
              contact_name: pod.supplierData.contact_name ?? null,
              email: pod.supplierData.email ?? null,
              start_of_supply: pod.supplierData.start_of_supply ?? null,
              end_of_supply: pod.supplierData.end_of_supply ?? null
            },

            pod_id: pod?.pod_id ?? null,
            available_capacity: parseFloat(pod.power_kw_available ?? '') ?? null,
            voltage: parseFloat(pod.voltage ?? '') ?? null,
            excise_taxes: parseFloat(pod.excise_taxes ?? '') ?? null,
            vat_rate: parseFloat(pod.vat_rate ?? '') ?? null,
            vat_amount_actual: parseFloat(pod.vat_amount_actual ?? '') ?? null
          } as InvoiceElectricityExtractedData;
        }),
        historical_data: []
      }
    };

    return invoiceData;
  }

  getElectricityInvoiceFromInputs(): ElectricityInvoice {
    const selectedLocation = this.navigationShareService.getState('invoiceReviewLocation');
    const base = this.invoice;

    return {
      // BaseInvoiceGet
      id: base?.id ?? '',
      created_at: base?.created_at ?? new Date().toISOString(),
      file: base?.file as FileResponse,
      company_id: base?.company_id ?? '',
      location_id: base?.location_id ?? null,
      status: base?.status ?? InvoiceStatus.DONE,
      for_period: base?.for_period ?? this.invoice?.for_period ?? null,
      invoice_number: this.invoiceNumberInput ?? base?.invoice_number ?? null,
      supplier_name: base?.supplier_name ?? null,

      total_consumption: this.utilsService.numberOrNull(this.totalConsumptionInput),
      invoice_amount_net_actual: this.utilsService.numberOrNull(this.invoiceAmountActualInput),
      invoice_amount_gross_actual: this.utilsService.numberOrNull(this.invoiceAmountGrossInput),

      total_taxes: base?.total_taxes ?? null,
      tms_charges: base?.tms_charges ?? null,

      is_consumption_ok: base?.is_consumption_ok ?? false,
      is_taxes_ok: base?.is_taxes_ok ?? false,
      is_invoice_amount_ok: base?.is_invoice_amount_ok ?? false,
      is_vat_amount_ok: base?.is_invoice_amount_ok ?? false,
      is_invoice_amount_gross_ok: base?.is_invoice_amount_gross_ok ?? false,

      total_discount_amount: this.utilsService.numberOrNull(this.totalDiscountInput) ?? base?.total_discount_amount ?? null,
      vat_rate: this.utilsService.numberOrNull(this.vatRateInput) ?? base?.vat_rate ?? null,
      vat_amount_actual: this.utilsService.numberOrNull(this.vatAmountInput) ?? base?.vat_amount_actual ?? null,
      nontaxable_amount: this.utilsService.numberOrNull(this.nontaxableAmountInput) ?? base?.nontaxable_amount ?? null,

      consumption_class:
        (this.selectedConsumptionClassDropdown != null
          ? this.consumptionClassDropdownOptions[this.selectedConsumptionClassDropdown]
          : base?.consumption_class) ?? null,

      visibility_status: base?.visibility_status ?? VisibilityStatus.UNDER_ADMIN_REVIEW,
      admin_notes: (this.adminNotesInput ?? base?.admin_notes) ?? null,
      expected_vat_rate: base?.expected_vat_rate ?? null,
      additional_charges: base?.additional_charges ?? null,

      admin_approved: base?.admin_approved ?? false,
      review_requested: base?.review_requested ?? false,

      // Pods - InvoiceElectricityPODGet
      pods: (this.editedPods ?? []).map((pod: any, i: number) => {
        const orig = base?.pods?.[i];

        const selectedContractType =
          pod.selectedContractTypeDropdown != null
            ? (this.contractTypeDropdownOptions[pod.selectedContractTypeDropdown]?.toUpperCase() as ContractType | undefined)
            : undefined;

        const selectedPriceType =
          pod.selectedPriceTypeDropdown != null
            ? this.priceTypeDropdownOptions[pod.selectedPriceTypeDropdown]?.toUpperCase() ?? null
            : null;

        const selectedNetworkTension =
          pod.selectedNetworkTensionDropdown != null
            ? this.networkTensionDropdownOptions[pod.selectedNetworkTensionDropdown]?.toUpperCase() ?? null
            : null;

        const selectedConsumptionClass =
          pod.selectedConsumptionClassDropdown != null
            ? this.consumptionClassDropdownOptions[pod.selectedConsumptionClassDropdown]?.toUpperCase() ?? null
            : null;

        return {
          id: orig?.id ?? null,

          contract_type: selectedContractType ?? orig?.contract_type ?? null,
          tariff_code: pod?.tariff_code ?? orig?.tariff_code ?? null,
          consumption_class: selectedConsumptionClass ?? orig?.consumption_class ?? null,

          month_consumption_f1: this.utilsService.numberOrNull(pod.month_consumption_f1),
          month_consumption_f2: this.utilsService.numberOrNull(pod.month_consumption_f2),
          month_consumption_f3: this.utilsService.numberOrNull(pod.month_consumption_f3),
          month_consumption_f0: this.utilsService.numberOrNull(pod.month_consumption_f0),
          month_consumption_p: this.utilsService.numberOrNull(pod.month_consumption_p),
          month_consumption_po: this.utilsService.numberOrNull(pod.month_consumption_po),

          price_type: selectedPriceType ?? orig?.price_type ?? null,

          pun_price_f1: this.utilsService.numberOrNull(pod.pun_price_f1),
          pun_price_f2: this.utilsService.numberOrNull(pod.pun_price_f2),
          pun_price_f3: this.utilsService.numberOrNull(pod.pun_price_f3),
          pun_price_f0: this.utilsService.numberOrNull(pod.pun_price_f0),
          pun_price_p: this.utilsService.numberOrNull(pod.pun_price_p),
          pun_price_po: this.utilsService.numberOrNull(pod.pun_price_po),

          spread_f1: this.utilsService.numberOrNull(pod.spread_f1),
          spread_f2: this.utilsService.numberOrNull(pod.spread_f2),
          spread_f3: this.utilsService.numberOrNull(pod.spread_f3),
          spread_f0: this.utilsService.numberOrNull(pod.spread_f0),
          spread_p: this.utilsService.numberOrNull(pod.spread_p),
          spread_po: this.utilsService.numberOrNull(pod.spread_po),

          price_var_f1: this.utilsService.numberOrNull(pod.price_var_f1),
          price_var_f2: this.utilsService.numberOrNull(pod.price_var_f2),
          price_var_f3: this.utilsService.numberOrNull(pod.price_var_f3),
          price_var_f0: this.utilsService.numberOrNull(pod.price_var_f0),
          price_var_p: this.utilsService.numberOrNull(pod.price_var_p),
          price_var_po: this.utilsService.numberOrNull(pod.price_var_po),

          price_fix_f1: this.utilsService.numberOrNull(pod.price_fix_f1),
          price_fix_f2: this.utilsService.numberOrNull(pod.price_fix_f2),
          price_fix_f3: this.utilsService.numberOrNull(pod.price_fix_f3),
          price_fix_f0: this.utilsService.numberOrNull(pod.price_fix_f0),
          price_fix_p: this.utilsService.numberOrNull(pod.price_fix_p),
          price_fix_po: this.utilsService.numberOrNull(pod.price_fix_po),

          network_tension: selectedNetworkTension ?? orig?.network_tension ?? null,
          tension_factor: this.utilsService.numberOrNull(pod.tension_factor),
          power_kw_available: this.utilsService.numberOrNull(pod.power_kw_available),
          power_kw_contracted: this.utilsService.numberOrNull(pod.power_kw_contracted),
          power_kw_max_consumption: this.utilsService.numberOrNull(pod.power_kw_max_consumption),
          dispatch_costs: this.utilsService.numberOrNull(pod.dispatch_costs),
          dispatching_factor: this.utilsService.numberOrNull(pod.dispatching_factor),

          market_capacity_p: this.utilsService.numberOrNull(pod.market_capacity_p),
          market_capacity_op: this.utilsService.numberOrNull(pod.market_capacity_op),
          fixed_charge: this.utilsService.numberOrNull(pod.fixed_charge),
          energy_charge_price: this.utilsService.numberOrNull(pod.energy_charge_price),
          power_charge_price: this.utilsService.numberOrNull(pod.power_charge_price),

          reactive_energy_costs: this.utilsService.numberOrNull(pod.reactive_energy_costs),
          fixed_costs: this.utilsService.numberOrNull(pod.fixed_costs),
          additional_charges: this.utilsService.numberOrNull(pod.additional_charges),

          total_discount_amount: this.utilsService.numberOrNull(pod.total_discount_amount) ?? orig?.total_discount_amount ?? null,
          taxrate_1: this.utilsService.numberOrNull(pod.taxrate_1),
          taxrate_2: this.utilsService.numberOrNull(pod.taxrate_2),

          modulation_charge: this.utilsService.numberOrNull(pod.modulation_charge),

          is_price_var_f1_ok: orig?.is_price_var_f1_ok ?? false,
          is_price_var_f2_ok: orig?.is_price_var_f2_ok ?? false,
          is_price_var_f3_ok: orig?.is_price_var_f3_ok ?? false,
          is_price_var_f0_ok: orig?.is_price_var_f0_ok ?? false,
          is_price_var_p_ok: orig?.is_price_var_p_ok ?? false,
          is_price_var_po_ok: orig?.is_price_var_po_ok ?? false,
          is_tms_charges_ok: orig?.is_tms_charges_ok ?? false,

          calc_data: orig?.calc_data ?? null,

          supplier: {
            id: orig?.supplier?.id ?? null,
            name: pod?.supplierData?.name ?? orig?.supplier?.name ?? null,
            vat_id: pod?.supplierData?.vat_id ?? orig?.supplier?.vat_id ?? null,
            contact_number: pod?.supplierData?.contact_number ?? orig?.supplier?.contact_number ?? null,
            contact_name: pod?.supplierData?.contact_name ?? orig?.supplier?.contact_name ?? null,
            email: pod?.supplierData?.email ?? orig?.supplier?.email ?? null,
            start_of_supply: pod?.supplierData?.start_of_supply ? this.dateConversion.dateToIso(pod?.supplierData?.start_of_supply) : orig?.supplier?.start_of_supply ?? null,
            end_of_supply: pod?.supplierData?.end_of_supply ? this.dateConversion.dateToIso((pod?.supplierData?.end_of_supply)) : orig?.supplier?.end_of_supply ?? null,
          } as SupplierGet,

          pod_id: pod?.pod_id ?? orig?.pod_id ?? null,
          available_capacity: this.utilsService.numberOrNull(pod.power_kw_available),
          voltage: this.utilsService.numberOrNull(pod.voltage),
          excise_taxes: this.utilsService.numberOrNull(pod.excise_taxes),
          vat_rate: this.utilsService.numberOrNull(pod.vat_rate),
          vat_amount_actual: this.utilsService.numberOrNull(pod.vat_amount_actual),
          tms_charges: this.utilsService.numberOrNull(pod.tms_charges),
          total_taxes: this.utilsService.numberOrNull(pod.total_taxes),
          additional_note_fields: this.getAdditionalNoteFieldsFromInputs(i, pod.additional_note_fields)
        } as InvoiceElectricityPODGet;
      }),
    };
  }

  onContractTypeChange(podIndex: number): void {
    this.monthConsumptionF1InputWarnings[podIndex] = null;
    this.monthConsumptionF2InputWarnings[podIndex] = null;
    this.monthConsumptionF3InputWarnings[podIndex] = null;
    this.priceVarF1InputWarnings[podIndex] = null;
    this.priceVarF2InputWarnings[podIndex] = null;
    this.priceVarF3InputWarnings[podIndex] = null;
    this.priceVarF0InputWarnings[podIndex] = null;
    this.monthConsumptionPInputWarnings[podIndex] = null;
    this.monthConsumptionPoInputWarnings[podIndex] = null;
    this.priceVarPInputWarnings[podIndex] = null;
    this.priceVarPoInputWarnings[podIndex] = null;

    this.validatePodRequiredFields(podIndex);
  }

  onPriceTypeChange(podIndex: number): void {
    this.priceVarF1InputWarnings[podIndex] = null;
    this.priceVarF2InputWarnings[podIndex] = null;
    this.priceVarF3InputWarnings[podIndex] = null;
    this.priceVarF0InputWarnings[podIndex] = null;
    this.priceVarPInputWarnings[podIndex] = null;
    this.priceVarPoInputWarnings[podIndex] = null;

    this.validatePodRequiredFields(podIndex);
  }

  validatePodRequiredFields(podIndex: number): void {
    const pod = this.editedPods[podIndex];
    if (!pod) return;

    this.tmsChargesInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.tms_charges || '');
    this.tensionFactorInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.tension_factor || '');
    this.networkTensionInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.network_tension || '');
    this.energyChargePriceInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.energy_charge_price || '');
    this.powerChargePriceInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.power_charge_price || '');
    this.totalTaxesInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.total_taxes || '');
    this.taxRate1InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.taxrate_1 || '');
    this.taxRate2InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.taxrate_2 || '');

    if (this.checkNetworkTension(podIndex, NetworkTensionType.BT)) {
      this.powerKwContractedInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.power_kw_contracted || '');
    }

    if (this.checkNetworkTension(podIndex, NetworkTensionType.MT)) {
      this.powerKwMaxConsumptionInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.power_kw_max_consumption || '');
    }

    if (this.checkContractType(podIndex, ContractType.FASCIA)) {
      this.monthConsumptionF1InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.month_consumption_f1 || '');
      this.monthConsumptionF2InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.month_consumption_f2 || '');
      this.monthConsumptionF3InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.month_consumption_f3 || '');

      if (this.checkPriceType(podIndex, this.VARIABLE_PRICE_TYPES)) {
        this.validatePriceVarFieldsForPod(podIndex);
      }
      else if (this.checkPriceType(podIndex, this.FIX_PRICE_TYPES)) {
        this.priceFixF1InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_fix_f1 || '');
        this.priceFixF2InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_fix_f2 || '');
        this.priceFixF3InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_fix_f3 || '');
      }
    }

    if (this.checkContractType(podIndex, ContractType.MONO)) {
      this.monthConsumptionF0InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.month_consumption_f0 || '');

      if (this.checkPriceType(podIndex, this.VARIABLE_PRICE_TYPES)) {
        this.validatePriceVarFieldsForPod(podIndex);
      }

      if (this.checkPriceType(podIndex, this.FIX_PRICE_TYPES)) {
        this.priceFixF0InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_fix_f0 || '');
      }
    }
    else if (this.checkContractType(podIndex, ContractType.PICCO)) {
      this.monthConsumptionPInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.month_consumption_p || '');
      this.monthConsumptionPoInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.month_consumption_po || '');

      if (this.checkPriceType(podIndex, this.VARIABLE_PRICE_TYPES)) {
        this.validatePriceVarFieldsForPod(podIndex);
      }
      else if (this.checkPriceType(podIndex, this.FIX_PRICE_TYPES)) {
        this.priceFixPInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_fix_p || '');
        this.priceFixPoInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_fix_po || '');
      }
    }

    if (pod.selectedContractTypeDropdown === undefined) {
      this.contractTypeDropdownWarning[podIndex] = this.transloco.translate('shared.validators.common.mandatory_field');
    } else {
      this.contractTypeDropdownWarning[podIndex] = null;
    }

    if (pod.selectedPriceTypeDropdown === undefined) {
      this.priceTypeDropdownWarning[podIndex] = this.transloco.translate('shared.validators.common.mandatory_field');
    } else {
      this.priceTypeDropdownWarning[podIndex] = null;
    }

    if (pod.selectedNetworkTensionDropdown === undefined) {
      this.networkTensionDropdownWarning[podIndex] = this.transloco.translate('shared.validators.common.mandatory_field');
    } else {
      this.networkTensionDropdownWarning[podIndex] = null;
    }
  }

  onFieldChange({ value, podIndex, targetArray, fieldName }: { value: number; podIndex?: number; targetArray?: (string | null)[]; fieldName?: string; }): void {
    if (podIndex === undefined) {
      switch (fieldName) {
        case 'totalConsumption':
          this.totalConsumptionInputWarning = value !== 0 ? null : this.validator.validateRecommendedField('');
          break;
        case 'invoiceAmountActual':
          this.invoiceAmountActualInputWarning = value !== 0 ? null : this.validator.validateRecommendedField('');
          break;
      }
      return;
    }

    if (podIndex !== undefined) {
      let warning;

      if (fieldName && fieldName === "power_kw_contracted") {
        if (this.checkNetworkTension(podIndex, NetworkTensionType.BT)) {
          warning = value !== 0 ? null : this.validator.validateRecommendedField('');
        }
      }
      else if (fieldName && fieldName === 'power_kw_max_consumption') {
        if (this.checkNetworkTension(podIndex, NetworkTensionType.MT)) {
          warning = value !== 0 ? null : this.validator.validateRecommendedField('');
        }
      }
      else if (fieldName && (fieldName.startsWith('pun_price_') || fieldName.startsWith('spread_') || fieldName.startsWith('price_var'))) {
        this.validatePriceVarFieldsForPod(podIndex);
      }
      else {
        warning = value !== 0 ? null : this.validator.validateRecommendedField('');
      }

      if (targetArray) {
        targetArray[podIndex] = warning ?? null;
      }
    }
  }

  onNetworkTensionChange(fieldName: string, value: string, podIndex?: number): void {
    if (podIndex !== undefined) {
      switch (fieldName) {
        case 'networkTension':
          if (value == NetworkTensionType.BT) {
            this.powerKwContractedInputWarnings[podIndex] = this.validator.validateRecommendedField(this.editedPods[podIndex].power_kw_contracted || '');
            this.powerKwMaxConsumptionInputWarnings[podIndex] = null;
            this.networkTensionInputWarnings[podIndex] = null;
          }
          else if (value == NetworkTensionType.MT) {
            this.powerKwMaxConsumptionInputWarnings[podIndex] = this.validator.validateRecommendedField(this.editedPods[podIndex].power_kw_max_consumption || '');
            this.powerKwContractedInputWarnings[podIndex] = null
            this.networkTensionInputWarnings[podIndex] = null;
          }
          else if (value.length === 0) {
            this.powerKwContractedInputWarnings[podIndex] = null
            this.powerKwMaxConsumptionInputWarnings[podIndex] = null;
            this.networkTensionInputWarnings[podIndex] = this.validator.validateRecommendedField('');
          } else {
            this.powerKwContractedInputWarnings[podIndex] = null
            this.powerKwMaxConsumptionInputWarnings[podIndex] = null;
            this.networkTensionInputWarnings[podIndex] = null;
          }
          break;
      }
    }
  }

  checkContractType(podIndex: number, contractType: string): boolean {
    const pod = this.editedPods[podIndex];
    return pod?.selectedContractTypeDropdown !== undefined &&
      this.contractTypeDropdownOptions[pod.selectedContractTypeDropdown] === contractType;
  }

  checkNetworkTension(podIndex: number, networkTension: string): boolean {
    const pod = this.editedPods[podIndex];
    if (pod?.selectedNetworkTensionDropdown === undefined) return false;
    const selectedNetworkTension = this.networkTensionDropdownOptions[pod.selectedNetworkTensionDropdown];
    return networkTension.includes(selectedNetworkTension);
  }

  checkPriceType(podIndex: number, priceTypes: string[]): boolean {
    const pod = this.editedPods[podIndex];
    if (pod?.selectedPriceTypeDropdown === undefined) return false;
    const selectedPriceType = this.priceTypeDropdownOptions[pod.selectedPriceTypeDropdown];
    return priceTypes.includes(selectedPriceType);
  }

  validateRequiredFields(): void {
    //Validate Invoice fields
    this.invoiceNumberInputError = this.validator.validateLength(this.invoiceNumberInput, 1, 100);
    this.totalConsumptionInputWarning = this.validator.validateRecommendedField(this.totalConsumptionInput);
    this.invoiceAmountActualInputWarning = this.validator.validateRecommendedField(this.invoiceAmountActualInput);
    if (this.selectedLocationDropdown === undefined) {
      this.locationDropdownError = this.transloco.translate('shared.validators.common.required');
    } else {
      this.locationDropdownError = null;
    }

    if (this.forPeriodDate) {
      const formatted = this.dateConversion.isoToFormattedDate(this.forPeriodDate.toISOString());
      this.forPeriodInputError = this.validator.validateDate(formatted);
    } else {
      this.forPeriodInputError = this.transloco.translate('shared.validators.common.required');
    }

    // Validate POD fields
    this.editedPods.forEach((pod, index) => {
      this.validatePodRequiredFields(index);
    });

  }

  validateInputs(): boolean {
    if (this.navigationShareService.getState("invoiceReviewLocation") === undefined) {
      this.toast.showToast('shared.error', 'shared.toast.select_location', 'error', 4000, true);
      return false;
    }

    for (const pod of this.editedPods) {
      if (pod.selectedContractTypeDropdown === undefined) {
        this.toast.showToast('shared.error', 'invoice.placeholders.contract_type', 'error', 4000, true);
        return false;
      }

      if (pod.selectedPriceTypeDropdown === undefined) {
        this.toast.showToast('shared.error', 'invoice.placeholders.price_type', 'error', 4000, true);
        return false;
      }

      if (pod.selectedNetworkTensionDropdown === undefined) {
        this.toast.showToast('shared.error', 'invoice.placeholders.network_tension', 'error', 4000, true);
        return false;
      }
    }

    this.invoiceNumberInputError = this.validator.validateLength(this.invoiceNumberInput, 1, 100);
    this.totalConsumptionInputError = this.validator.validateNumber(this.totalConsumptionInput, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, false, false);
    this.invoiceAmountActualInputError = this.validator.validateNumber(this.invoiceAmountActualInput, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, false, false);
    this.invoiceAmountGrossInputError = this.validator.validateNumber(this.invoiceAmountGrossInput, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, false, false);
    this.totalDiscountInputError = this.validator.validateNumber(this.totalDiscountInput, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, false, false);
    this.vatRateInputError = this.validator.validateNumber(this.vatRateInput, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, false, false);
    this.vatAmountInputError = this.validator.validateNumber(this.vatAmountInput, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, false, false);
    this.nontaxableAmountInputError = this.validator.validateNumber(this.nontaxableAmountInput, Number.MIN_SAFE_INTEGER, Number.MAX_SAFE_INTEGER, false, false);
    this.adminNotesInputError = this.validator.validateLength(this.adminNotesInput, 2, 1000, false)

    if (this.forPeriodDate) {
      const formatted = this.dateConversion.isoToFormattedDate(this.forPeriodDate.toISOString());
      this.forPeriodInputError = this.validator.validateDate(formatted);
    } else {
      this.forPeriodInputError = this.transloco.translate('invoice.errors.invalid_date');
    }

    return !(
      this.invoiceNumberInputError ||
      this.totalConsumptionInputError ||
      this.invoiceAmountActualInputError ||
      this.invoiceAmountGrossInputError ||
      this.totalDiscountInputError ||
      this.forPeriodInputError ||
      this.vatRateInputError ||
      this.vatAmountInputError ||
      this.nontaxableAmountInputError ||
      this.adminNotesInputError
    );
  }

  async saveInvoice(): Promise<void> {
    this.saveClicked = true;

    if (!this.validateInputs()) {
      this.saveClicked = false;
      return;
    }

    this.savingInvoice = true;

    try {
      const response = await lastValueFrom(this.invoiceService.updateFullElectricityInvoice(this.getElectricityInvoiceDataFromInputs()))
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 4000, false);
      this.successful = false;
      this.saveClicked = false;
      this.savingInvoice = false;
      return;
    }

    // Save Additiona Fields
    await this.saveAdditionalNoteFields(this.invoice?.id || '');

    // Save File Changes in Log
    if (this.invoice) {
      await this.logInvoiceChanges(this.invoice, this.getElectricityInvoiceFromInputs());
    }

    this.successful = true;
    this.savingInvoice = false;
    this.cancelEditInvoiceData()
    this.reloadCallbackFunction();
  }

  getFieldsToIgnore(oldInvoice: ElectricityInvoice, newInvoice: ElectricityInvoice) {
    const ignoreFields = ['calc_data', 'admin_notes', 'company', 'vat_id', 'voltage', 'excise_taxes', 'is_vat_amount_ok', 'id'];

    // Special check to avoid false difference
    const normalize = (v?: string | null) => {
      if (v === null || v === undefined) return '';
      const t = String(v).trim();
      return t === 'None' ? '' : t;
    };
    const compare = (a?: string | null, b?: string | null) =>
      normalize(a).toLowerCase() === normalize(b).toLowerCase();

    // Flags
    let allConsumptionClassesEqual = true;
    let allSupplierNameEqual = true;
    let allSupplierVatIdEqual = true;
    let allSupplierContactNameEqual = true;
    let allSupplierContactNumberEqual = true;
    let allSupplierEmailEqual = true;

    // Go over pods
    if (oldInvoice.pods?.length === newInvoice.pods?.length) {
      for (let i = 0; i < oldInvoice.pods.length; i++) {
        const oldPod = oldInvoice.pods[i];
        const newPod = newInvoice.pods[i];

        // consumption_class
        if ((oldPod?.consumption_class ?? '').toUpperCase() !== (newPod?.consumption_class ?? '').toUpperCase()) {
          allConsumptionClassesEqual = false;
        }

        // supplier fields
        if (!compare(oldPod?.supplier?.name, newPod?.supplier?.name)) {
          allSupplierNameEqual = false;
        }
        if (!compare(oldPod?.supplier?.vat_id, newPod?.supplier?.vat_id)) {
          allSupplierVatIdEqual = false;
        }
        if (!compare(oldPod?.supplier?.contact_name, newPod?.supplier?.contact_name)) {
          allSupplierContactNameEqual = false;
        }
        if (!compare(oldPod?.supplier?.contact_number, newPod?.supplier?.contact_number)) {
          allSupplierContactNumberEqual = false;
        }
        if (!compare(oldPod?.supplier?.email, newPod?.supplier?.email)) {
          allSupplierEmailEqual = false;
        }

        if (
          !allConsumptionClassesEqual &&
          !allSupplierNameEqual &&
          !allSupplierVatIdEqual &&
          !allSupplierContactNameEqual &&
          !allSupplierContactNumberEqual &&
          !allSupplierEmailEqual
        ) break;
      }
    } else {
      allConsumptionClassesEqual = false;
      allSupplierNameEqual = false;
      allSupplierVatIdEqual = false;
      allSupplierContactNameEqual = false;
      allSupplierContactNumberEqual = false;
      allSupplierEmailEqual = false;
    }

    // Add ignore fields
    if (allConsumptionClassesEqual) ignoreFields.push('consumption_class');
    if (allSupplierNameEqual) ignoreFields.push('supplier.name');
    if (allSupplierVatIdEqual) ignoreFields.push('supplier.vat_id');
    if (allSupplierContactNameEqual) ignoreFields.push('supplier.contact_name');
    if (allSupplierContactNumberEqual) ignoreFields.push('supplier.contact_number');
    if (allSupplierEmailEqual) ignoreFields.push('supplier.email');

    return ignoreFields;
  }

  async logInvoiceChanges(oldInvoice: ElectricityInvoice, newInvoice: ElectricityInvoice) {
    const ignoreFields = this.getFieldsToIgnore(oldInvoice, newInvoice);
    const difference = this.utilsService.getObjectDifferences(oldInvoice, newInvoice, ignoreFields)

    if (difference == undefined) {
      return;
    }

    const message = `Admin ${this.userData.getUser()?.first_name} ${this.userData.getUser()?.last_name} (${this.userData.getUser()?.email}) saved new changes in invoice.`;
    const adminChangesLogData = this.utilsService.getAdminChangesLogData(difference, message, this.exprByField);

    // Save log
    try {
      const response = await lastValueFrom(this.invoiceService.sendFileChangesLog(oldInvoice.file.id, adminChangesLogData))
    } catch (error) {
      var crid = 'UNKNOWN-CRID';
      if (error instanceof HttpErrorResponse) {
        crid = error.headers?.get('x-correlation-id') || 'UNKNOWN-CRID';
      }
      this.toast.showToast(await this.apiError.getErrorHeader(), await this.apiError.getErrorMessage(crid), 'error', 4000, false);
    }
  }

  async saveAdditionalNoteFields(invoiceId: string) {
    var newInvoice = null;
    try {
      newInvoice = await lastValueFrom(this.invoiceService.getElectricityInvoiceById(invoiceId));
    } catch (error) {
      return;
    }

    if (newInvoice?.pods) {
      for (let i = 0; i < newInvoice.pods.length; i++) {
        const pod = newInvoice.pods[i];
        const additionalNotes = pod.additional_note_fields;

        try {
          const additionalFields = this.getAdditionalNoteFieldsFromInputs(i, additionalNotes);
          await lastValueFrom(this.invoiceService.saveAdditionalNoteFieldsElectricity(pod.id || '', additionalFields));
        }
        catch (error) {
          // pass
        }
      }
    }
  }

  copyText(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toast.showToast('shared.success', 'admin.invite_copied', 'success', 2000, true);
    }).catch(err => {
    });
  }

  copyJson() {
    var invoice = this.getElectricityInvoiceDataFromInputs();
    invoice.id = this.invoice?.file.id ?? '';
    const json = JSON.stringify(invoice, null, 2);
    this.copyText(json);
  }

  downloadJson() {
    var invoice = this.getElectricityInvoiceDataFromInputs();
    invoice.id = this.invoice?.file.id ?? '';
    const json = JSON.stringify(invoice, null, 2);

    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;

    const downloadedFileName = `${this.invoice?.file?.filename?.replace(/\.[^.]+$/, '')}.json` || `electricity_invoice-${new Date().toISOString()}.json`;
    a.download = downloadedFileName;

    a.click();

    URL.revokeObjectURL(url);
  }


  checkMandatoryVariableEmptyStatus(variable: any, showOkStatus: boolean = false): InvoiceVariableStatus | undefined {
    if (variable === null || variable === undefined || variable === '') {
      this.mandatoryDataMissing = true;
      return InvoiceVariableStatus.MANDATORY_FIELD_MISSING;
    }

    if (!showOkStatus) {
      return undefined;
    }

    return InvoiceVariableStatus.OK;
  }

  checkPunPriceLookupStatus(value: any, allPunValues: any[]): InvoiceVariableStatus | undefined {
    const isEmpty = (v: any) => v === null || v === undefined || v === '' || Number(v) === 0;
    const hasAnyValue = allPunValues.some(v => !isEmpty(v));
    const thisIsEmpty = isEmpty(value);

    if (hasAnyValue && thisIsEmpty) {
      return InvoiceVariableStatus.INCOMPLETE_LOOKUP_DATA;
    }

    return this.checkVariableEmptyStatus(value);
  }

  checkVariableEmptyStatus(variable: any, showOkStatus: boolean = false): InvoiceVariableStatus | undefined {
    if (variable === null || variable === undefined || variable === '') {
      return InvoiceVariableStatus.OPTIONAL_FIELD_EMPTY;
    }

    if (!showOkStatus) {
      return undefined;
    }

    return InvoiceVariableStatus.OK;
  }

  getInvoiceDeltaStatus(calculationNumber: number | null | undefined, extractionNumber: number | null | undefined, thresholdPercent = 5, warnExtractionEmpty = true): InvoiceVariableStatus | undefined {
    if (warnExtractionEmpty && (extractionNumber === null || extractionNumber === undefined)) {
      return InvoiceVariableStatus.WARNING;
    }

    const calc = Math.round((calculationNumber ?? 0) * 100) / 100;
    const ext = Math.round((extractionNumber ?? 0) * 100) / 100;
    const delta = Math.round((ext - calc) * 100) / 100;

    if (delta === 0) {
      return undefined;
    }

    // diff
    const relativeDiff = calc !== 0 ? Math.abs(delta) / Math.abs(calc) * 100 : Math.abs(delta) * 100; // fallback if calc=0

    if (relativeDiff < thresholdPercent) {
      return undefined;
    }

    return InvoiceVariableStatus.ERROR;
  }

  loadFileSummary() {
    const fileId = this.invoice?.file.id ?? '';
    this.loadingFileSummary = true;
    this.fileDetailsResponse = null;

    this.filesService.getFileFullData(fileId).subscribe({
      next: (response) => {
        this.fileDetailsResponse = response;
        this.loadingFileSummary = false;
      },
      error: () => {
        this.toast.showToast("shared.error", "admin.not_found", "error", 3000, true);
        this.loadingFileSummary = false;
      }
    });
  }

  onFileSummaryOpen(isOpen: boolean) {
    if (isOpen) {
      this.loadFileSummary();
    }
  }

  editInvoiceData() {
    this.isEditingInvoiceData = true;
  }

  cancelEditInvoiceData() {
    this.isEditingInvoiceData = false;
    this.resetInvoiceData();
  }

  saveEditInvoiceData() {
    // Temp reset mandatory data missing
    this.mandatoryDataMissing = false;

    this.saveInvoice();
  }

  safeValue(value: any, format: string = '1.2-2'): any {
    if (value !== null && value !== undefined) {
      return this.decimalPipe.transform(value, format);
    }
    return '';
  }

  getContractType(selectedContractTypeDropdown: number | undefined) {
    if (selectedContractTypeDropdown === undefined) {
      return null;
    }
    return this.contractTypeDropdownOptions[selectedContractTypeDropdown].toUpperCase() as ContractType;
  }

  getPriceType(selectedPriceTypeDropdown: number | undefined) {
    if (selectedPriceTypeDropdown === undefined) {
      return null;
    }
    return this.priceTypeDropdownOptions[selectedPriceTypeDropdown].toUpperCase() as PriceType;
  }

  isOneOf<T>(value: T | null | undefined, list: T[]): boolean {
    if (value == null) return false;
    return list.includes(value);
  }

  handleMissingDataClick() {
    if (this.invoiceDataDropdown == undefined) { return; }

    if (!this.invoiceDataDropdown.isOpen) {
      this.invoiceDataDropdown.toggle();
    }

    this.editInvoiceData();
    this.invoiceDataDropdownEl?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  isAnyMandatoryVariableMissing(): boolean {
    return this.mandatoryDataMissing;
  }

  private isFieldEmpty(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  getPriceVarFasciaStatus(pun_f1: any, spread_f1: any, pun_f2: any, spread_f2: any, pun_f3: any, spread_f3: any, price_var: any, priceType?: PriceType | null): InvoiceVariableStatus | undefined {
    if (priceType === PriceType.VAR_PUNH) {
      const status = this.checkMandatoryVariableEmptyStatus(price_var);
      return status ?? InvoiceVariableStatus.MANDATORY_FIELD_MISSING;
    }

    const hasAllPunAndSpread =
      !this.isFieldEmpty(pun_f1) && !this.isFieldEmpty(spread_f1) &&
      !this.isFieldEmpty(pun_f2) && !this.isFieldEmpty(spread_f2) &&
      !this.isFieldEmpty(pun_f3) && !this.isFieldEmpty(spread_f3);

    const status = hasAllPunAndSpread
      ? this.checkVariableEmptyStatus(price_var)
      : this.checkMandatoryVariableEmptyStatus(price_var);

    return status;
  }

  getPriceVarMonoStatus(pun_f0: any, spread_f0: any, price_var_f0: any, priceType?: PriceType | null): InvoiceVariableStatus | undefined {
    if (priceType === PriceType.VAR_PUNH) {
      const status = this.checkMandatoryVariableEmptyStatus(price_var_f0);
      return status ?? InvoiceVariableStatus.MANDATORY_FIELD_MISSING;
    }
    const hasPunAndSpread =
      !this.isFieldEmpty(pun_f0) && !this.isFieldEmpty(spread_f0);

    const status = hasPunAndSpread
      ? this.checkVariableEmptyStatus(price_var_f0)
      : this.checkMandatoryVariableEmptyStatus(price_var_f0);

    return status;
  }

  getPriceVarPiccoStatus(pun_p: any, spread_p: any, pun_po: any, spread_po: any, price_var: any, priceType?: PriceType | null): InvoiceVariableStatus | undefined {
    if (priceType === PriceType.VAR_PUNH) {
      const status = this.checkMandatoryVariableEmptyStatus(price_var);
      return status ?? InvoiceVariableStatus.MANDATORY_FIELD_MISSING;
    }
    const hasAllPunAndSpread =
      !this.isFieldEmpty(pun_p) && !this.isFieldEmpty(spread_p) &&
      !this.isFieldEmpty(pun_po) && !this.isFieldEmpty(spread_po);

    const status = hasAllPunAndSpread
      ? this.checkVariableEmptyStatus(price_var)
      : this.checkMandatoryVariableEmptyStatus(price_var);

    return status;
  }

  validatePriceVarFieldsForPod(podIndex: number): void {
    const pod = this.editedPods[podIndex];
    if (!pod) return;

    const priceType = this.getPriceType(pod.selectedPriceTypeDropdown);

    if (this.checkContractType(podIndex, ContractType.FASCIA)) {
      if (priceType === PriceType.VAR || priceType === PriceType.VAR_PUN) {
        const hasAllPunAndSpread =
          !this.isFieldEmpty(pod.pun_price_f1) && !this.isFieldEmpty(pod.spread_f1) &&
          !this.isFieldEmpty(pod.pun_price_f2) && !this.isFieldEmpty(pod.spread_f2) &&
          !this.isFieldEmpty(pod.pun_price_f3) && !this.isFieldEmpty(pod.spread_f3);

        if (!hasAllPunAndSpread) {
          this.priceVarF1InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_f1 || '');
          this.priceVarF2InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_f2 || '');
          this.priceVarF3InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_f3 || '');
        } else {
          this.priceVarF1InputWarnings[podIndex] = null;
          this.priceVarF2InputWarnings[podIndex] = null;
          this.priceVarF3InputWarnings[podIndex] = null;
        }
      }
      else if (priceType === PriceType.VAR_PUNH) {
        this.priceVarF1InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_f1 || '');
        this.priceVarF2InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_f2 || '');
        this.priceVarF3InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_f3 || '');
      }
    }


    if (this.checkContractType(podIndex, ContractType.MONO)) {
      if (priceType === PriceType.VAR || priceType === PriceType.VAR_PUN) {
        const hasPunAndSpread = !this.isFieldEmpty(pod.pun_price_f0) && !this.isFieldEmpty(pod.spread_f0);

        if (!hasPunAndSpread) {
          this.priceVarF0InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_f0 || '');
        } else {
          this.priceVarF0InputWarnings[podIndex] = null;
        }
      }
      else if (priceType === PriceType.VAR_PUNH) {
        this.priceVarF0InputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_f0 || '');
      }
    }

    if (this.checkContractType(podIndex, ContractType.PICCO)) {
      if (priceType === PriceType.VAR || priceType === PriceType.VAR_PUN) {
        const hasAllPunAndSpread =
          !this.isFieldEmpty(pod.pun_price_p) && !this.isFieldEmpty(pod.spread_p) &&
          !this.isFieldEmpty(pod.pun_price_po) && !this.isFieldEmpty(pod.spread_po);

        if (!hasAllPunAndSpread) {
          this.priceVarPInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_p || '');
          this.priceVarPoInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_po || '');
        } else {
          this.priceVarPInputWarnings[podIndex] = null;
          this.priceVarPoInputWarnings[podIndex] = null;
        }
      }
      else if (priceType === PriceType.VAR_PUNH) {
        this.priceVarPInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_p || '');
        this.priceVarPoInputWarnings[podIndex] = this.validator.validateRecommendedField(pod.price_var_po || '');
      }
    }
  }

  onExprChange(fieldKey: string, index: number, expr: string | null) {
    const id = `${fieldKey}#${index}`;

    if (expr && expr.trim() !== '=' && expr.trim() !== '') {
      this.exprByField[id] = expr.trim();
    } else {
      delete this.exprByField[id];
    }
  }

  openPodSection(index: number) {
    if (!this.podSectionsDropdown) { return; }
    const sections = this.podSectionsDropdown.toArray();
    const section = sections[index];

    if (section && !section.isOpen) {
      section.toggle();
    }
  }


  scroolToSpread() {
    if (!this.isOneOf(this.getPriceType(this.editedPods[0].selectedPriceTypeDropdown), [PriceType.VAR,
    PriceType.VAR_PUN,]) || this.viewAllComponents) { return; }

    setTimeout(() => {
      // Open first pod section
      this.openPodSection(0);
      this.isTotalMaterialCollapsed = true;

      // Toggle editing
      this.editInvoiceData();

      this.cd.detectChanges();

      // Scroll to spread
      const contractType = this.getContractType(this.editedPods[0].selectedContractTypeDropdown);

      // Element based on type
      let targetEl: ElementRef | undefined;

      if (this.viewAllComponents || contractType === ContractType.MONO) {
        targetEl = this.spreadf0Element;
      } else if (contractType === ContractType.FASCIA) {
        targetEl = this.spreadf1Element;
      } else if (contractType === ContractType.PICCO) {
        targetEl = this.spreadpElement;
      }
      if (!targetEl) return;

      const el = targetEl.nativeElement;

      // Scroll and animate
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('animate-highlight');

      setTimeout(() => {
        el.classList.remove('animate-spreadHighlight');
      }, 4000);

      this.cd.detectChanges();
    });
  }

  onPodSectionOpenedChange(index: number, isOpen: boolean) {
    if (isOpen) {
      this.openedPodSectionIndexes.add(index);
    } else {
      this.openedPodSectionIndexes.delete(index);
    }
  }

  restoreOpenedPodSections() {
    if (!this.podSectionsDropdown) { return; }

    const sections = this.podSectionsDropdown.toArray();
    this.openedPodSectionIndexes.forEach(i => {
      const section = sections[i];
      if (section && !section.isOpen) {
        section.toggle();
      }
    });
  }
}
